import React, { useEffect, useState } from 'react';
import { DivisionWithTagsDTO } from '@erp/shared-types';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import PrimaryButton from 'src/components/common/PrimaryButton';
import { TextField, IconButton, Chip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GentlyTable, { TableColumn } from 'src/components/common/GentlyTable';
import {
  useAliases,
  useUpsertAliases,
  useDeleteAlias,
} from 'src/api/core/aliases/useAliases';
import useUpdateDivision from 'src/api/organization/useUpdateDivision';
import useCreateDivision from 'src/api/organization/useCreateDivision';
import {
  useIsModuleEnabled,
  ProductModule,
} from 'src/hooks/organization-config/useOrganizationEnabledModules';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { useUpsertEnumOptionAssociationToEntity } from '@/api/core/enumOptionAssociations/useEnumOptionAssociations';

interface Props {
  division?: DivisionWithTagsDTO;
  onSuccess?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

const DivisionForm: React.FC<Props> = ({
  division,
  onSuccess,
  onCancel,
  showCancelButton,
}) => {
  const divisionId = division?.id ?? '';
  const { data: divisionBanners } = useCoreEnums({
    name: 'Banner',
    includeOptions: true,
  });

  const divisionBannersOptions = divisionBanners?.edges[0]?.options;

  // Editable fields
  const [name, setName] = useState<string>(division?.name ?? '');
  const [description, setDescription] = useState<string>(
    (division?.metadata?.description as string) ?? '',
  );
  const [geography, setGeography] = useState<string>(
    (division?.metadata?.geo as string) ?? '',
  );
  const [internalCode, setinternalCode] = useState<string>(
    (division?.metadata?.internal_code as string) ?? '',
  );

  const { data, refetch } = useAliases('division', divisionId, !!divisionId);
  const { mutateAsync: upsertAliases } = useUpsertAliases();
  const { mutateAsync: deleteAliasRequest } = useDeleteAlias();
  const { mutateAsync: updateDivision } = useUpdateDivision(divisionId, () =>
    onSuccess?.(),
  );
  const { mutateAsync: createDivision } = useCreateDivision();

  const { mutateAsync: updateDivisionBanners } =
    useUpsertEnumOptionAssociationToEntity();
  // Local editable aliases (defer persistence to Save)
  const [localAliases, setLocalAliases] = useState<string[]>([]);
  useEffect(() => {
    const initial = (data?.aliases ?? []).map((a) =>
      a.alias.replaceAll('|||', ' • '),
    );
    setLocalAliases(initial);
  }, [data]);

  const [newAlias, setNewAlias] = useState('');
  const [tags, setTags] = useState<string[]>(
    division?.tags?.map((t) => t.id) ?? [],
  );

  const handleToggleBanner = async (bannerId: string) => {
    const isSelected = tags.includes(bannerId);
    const updated = isSelected
      ? tags.filter((id) => id !== bannerId)
      : [...tags, bannerId];
    setTags(updated);

    try {
      await updateDivisionBanners({
        entityId: divisionId,
        enumOptionIds: isSelected
          ? tags.filter((id) => id !== bannerId)
          : [...tags, bannerId],
        entityType: 'division',
      });
    } catch (error) {
      console.error('Failed to update banners', error);
    }
  };

  // Alias table configuration
  type AliasRow = {
    id: string;
    alias: string;
    addedByUserName: string;
    createdAt: string;
  };

  const aliasColumns: TableColumn<AliasRow>[] = [
    {
      id: 'alias',
      label: 'Alias',
      sortable: true,
    },
    {
      id: 'addedByUserName',
      label: 'User',
      sortable: true,
    },
    {
      id: 'createdAt',
      label: 'Date',
      sortable: true,
      render: (value) => (
        <MDTypography variant="body2">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value))}
        </MDTypography>
      ),
    },
    {
      id: 'id',
      label: 'Actions',
      sortable: false,
      render: (value, row) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteAliasLocal(row.alias)}
          title="Delete Alias"
          color="error"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleAddAliasClick = () => {
    const trimmed = newAlias.trim();
    if (!trimmed) return;
    const norm = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
    const currentNorm = new Set(localAliases.map(norm));
    if (currentNorm.has(norm(trimmed))) {
      setNewAlias('');
      return;
    }
    setLocalAliases((prev) => [...prev, trimmed]);
    setNewAlias('');
  };

  const handleDeleteAliasLocal = (alias: string) => {
    setLocalAliases((prev) => prev.filter((a) => a !== alias));
  };

  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      const metadata: Record<string, unknown> = {
        ...(division?.metadata || {}),
        description,
        geo: geography,
      };

      if (!divisionId) {
        // Creating a new division
        await createDivision({ name, metadata });
        onSuccess?.();
        return;
      }

      // Updating existing division
      await updateDivision({ name, metadata });

      const original = new Set((data?.aliases ?? []).map((a) => a.alias));
      const current = new Set(localAliases);
      const toAdd: string[] = [];
      const toDelete: string[] = [];
      for (const a of current) if (!original.has(a)) toAdd.push(a);
      for (const a of original) if (!current.has(a)) toDelete.push(a);

      if (toAdd.length > 0) {
        await upsertAliases({
          entityType: 'division',
          entityId: divisionId,
          aliases: toAdd,
        });
      }
      if (toDelete.length > 0) {
        await Promise.all(
          toDelete.map((a) =>
            deleteAliasRequest({
              entityType: 'division',
              entityId: divisionId,
              alias: a,
            }),
          ),
        );
      }
      await refetch();
      onSuccess?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDBox>
      {/* Basic fields */}
      <MDBox mb={3} display="grid" gap={2}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          label="Geography"
          value={geography}
          onChange={(e) => setGeography(e.target.value)}
          fullWidth
        />
      </MDBox>

      {/* Aliases */}
      {division ? (
        <MDBox mb={3}>
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="bold"
            display="block"
            mb={2}
          >
            These combinations map to {division.name}
          </MDTypography>

          <MDBox sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            <GentlyTable
              data={localAliases.map((alias) => {
                const meta = (data?.aliases || []).find(
                  (x) => x.alias === alias,
                );
                return {
                  id: meta?.id || alias,
                  alias: alias,
                  addedByUserName: meta?.addedByUserName || 'system',
                  createdAt: meta?.createdAt || new Date().toISOString(),
                };
              })}
              columns={aliasColumns}
              searchPlaceholder="Search aliases..."
              emptyMessage="No aliases found for this division"
              searchFields={['alias', 'addedByUserName']}
              showSearch={localAliases.length > 5}
            />
          </MDBox>

          <MDBox display="flex" gap={1} mt={2}>
            <TextField
              size="small"
              placeholder="Add alias"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddAliasClick();
                }
              }}
              sx={{ flexGrow: 1 }}
            />
            <PrimaryButton onClick={handleAddAliasClick}>Add</PrimaryButton>
          </MDBox>
        </MDBox>
      ) : null}

      {/* Banners */}
      <MDBox mb={3}>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          display="block"
          mb={2}
        >
          Banners
        </MDTypography>
        <MDBox display="flex" gap={1} flexWrap="wrap">
          {divisionBannersOptions?.map((banner) => {
            const isSelected = tags.includes(banner.id);
            return (
              <Chip
                key={banner.id}
                label={banner.name}
                clickable
                onClick={() => handleToggleBanner(banner.id)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
              />
            );
          })}
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="flex-end" gap={2}>
        {showCancelButton ? (
          <PrimaryButton color="secondary" onClick={onCancel}>
            Cancel
          </PrimaryButton>
        ) : null}
        <PrimaryButton onClick={handleSave} disabled={saving}>
          Save
        </PrimaryButton>
      </MDBox>
    </MDBox>
  );
};

export default DivisionForm;
